import { z } from 'zod';
import { MarkupCategory, MarkupPurpose, UserRole, MarkupContext } from '../enums';

export { MarkupCategory };

export const MarkupCategorySchema = z.nativeEnum(MarkupCategory);
export type MarkupCategoryEnum = z.infer<typeof MarkupCategorySchema>;

export const MarkupPurposeSchema = z.nativeEnum(MarkupPurpose);
// Keep MarkupPurposeEnum as a value export (Zod schema) for backward compat with index.ts
export const MarkupPurposeEnum = MarkupPurposeSchema;
export type MarkupPurposeEnum = z.infer<typeof MarkupPurposeSchema>;

// GeoJSON geometry - supports Point, LineString, Polygon, MultiPoint
const GeoJsonCoordinate = z.tuple([z.number(), z.number()]); // [x, y] in pixel coords

const GeoJsonGeometry = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('Point'),
    coordinates: GeoJsonCoordinate,
  }),
  z.object({
    type: z.literal('MultiPoint'),
    coordinates: z.array(GeoJsonCoordinate),
  }),
  z.object({
    type: z.literal('LineString'),
    coordinates: z.array(GeoJsonCoordinate).min(2),
  }),
  z.object({
    type: z.literal('Polygon'),
    coordinates: z.array(z.array(GeoJsonCoordinate).min(4)), // ring: first == last
  }),
]);

export const MarkupSchema = z.object({
  id: z.string().uuid(),
  project_id: z.string().uuid(),
  knowledge_item_id: z.string().uuid().nullable(),
  page_id: z.string().uuid().nullable(),
  geometry: GeoJsonGeometry,
  label: z.string(),
  category: MarkupCategorySchema,
  color: z.string().nullable(),
  sort_order: z.number().int().nullable(),
  // AI values
  ai_area: z.number().nullable(),
  ai_length: z.number().nullable(),
  ai_width: z.number().nullable(),
  ai_height: z.number().nullable(),
  ai_perimeter: z.number().nullable(),
  ai_unit: z.string().nullable(),
  ai_confidence: z.number().nullable(),
  ai_source: z.string().nullable(),
  ai_notes: z.string().nullable(),
  // Overrides
  override_area: z.number().nullable(),
  override_length: z.number().nullable(),
  override_width: z.number().nullable(),
  override_height: z.number().nullable(),
  override_notes: z.string().nullable(),
  overridden_by: z.string().uuid().nullable(),
  overridden_at: z.string().nullable(),
  effective_area: z.number().nullable(),
  area_id: z.string().uuid().nullable(),
  revision_id: z.string().uuid().nullable(),
  author_id: z.string().uuid().nullable(),
  author_role: z.nativeEnum(UserRole).nullable(),
  markup_purpose: MarkupPurposeSchema.default(MarkupPurpose.MEASUREMENT),
  author_name: z.string().optional(),
  // Measurement type (for the full suite)
  measurement_type: z.enum(['area-rect', 'linear', 'polyline', 'area-polygon', 'angle', 'count']).nullable(),
  measured_value: z.number().nullable(),
  measured_unit: z.string().nullable(),
  item_category_id: z.string().uuid().nullable(),
  product_id: z.string().uuid().nullable(),
  markup_context: z.nativeEnum(MarkupContext).nullable(),
  // Timestamps
  created_at: z.string(),
  updated_at: z.string(),
});

export type Markup = z.infer<typeof MarkupSchema>;

export const CreateMarkupSchema = z.object({
  knowledge_item_id: z.string().uuid().optional(),
  page_id: z.string().uuid().optional(),
  geometry: GeoJsonGeometry,
  label: z.string(),
  category: MarkupCategorySchema,
  color: z.string().optional(),
  sort_order: z.number().int().optional(),
  measurement_type: z.enum(['area-rect', 'linear', 'polyline', 'area-polygon', 'angle', 'count']).nullable().optional(),
  measured_value: z.number().nullable().optional(),
  measured_unit: z.string().nullable().optional(),
  ai_area: z.number().optional(),
  ai_length: z.number().optional(),
  ai_width: z.number().optional(),
  ai_height: z.number().optional(),
  ai_perimeter: z.number().optional(),
  ai_unit: z.string().optional(),
  ai_confidence: z.number().optional(),
  ai_source: z.string().optional(),
  ai_notes: z.string().optional(),
  revision_id: z.string().uuid().optional(),
  author_id: z.string().uuid().optional(),
  author_role: z.nativeEnum(UserRole).optional(),
  markup_purpose: MarkupPurposeSchema.optional(),
  item_category_id: z.string().uuid().nullable().optional(),
  markup_context: z.nativeEnum(MarkupContext).nullable().optional(),
});

export type CreateMarkup = z.infer<typeof CreateMarkupSchema>;

export const UpdateMarkupSchema = z.object({
  geometry: GeoJsonGeometry.optional(),
  label: z.string().optional(),
  override_area: z.number().nullable().optional(),
  override_length: z.number().nullable().optional(),
  override_width: z.number().nullable().optional(),
  override_height: z.number().nullable().optional(),
  override_notes: z.string().nullable().optional(),
  measured_value: z.number().nullable().optional(),
  measured_unit: z.string().nullable().optional(),
  area_id: z.string().uuid().nullable().optional(),
  item_category_id: z.string().uuid().nullable().optional(),
  product_id: z.string().uuid().nullable().optional(),
  markup_context: z.nativeEnum(MarkupContext).nullable().optional(),
});

export type UpdateMarkup = z.infer<typeof UpdateMarkupSchema>;

export const BulkUpdateMarkupSchema = z.object({
  markups: z.array(
    z.object({
      id: z.string().uuid(),
      override_area: z.number().nullable().optional(),
      override_length: z.number().nullable().optional(),
      override_width: z.number().nullable().optional(),
      override_height: z.number().nullable().optional(),
      override_notes: z.string().nullable().optional(),
      measured_value: z.number().nullable().optional(),
      area_id: z.string().uuid().nullable().optional(),
      item_category_id: z.string().uuid().nullable().optional(),
      product_id: z.string().uuid().nullable().optional(),
    }),
  ),
});

export type BulkUpdateMarkup = z.infer<typeof BulkUpdateMarkupSchema>;
